import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Search, Shield, Save, CheckCircle2, AlertCircle } from 'lucide-react';

const PermissionsGrid = () => {
    const { authFetch } = useAuth();

    const [capabilities, setCapabilities] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Track row-level loading states
    const [savingUserId, setSavingUserId] = useState(null);
    const [saveStatus, setSaveStatus] = useState({ userId: null, success: false });

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [permRes, usersRes] = await Promise.all([
                authFetch('/api/backoffice/permissions-list'),
                authFetch('/api/backoffice/users')
            ]);

            if (permRes.ok) {
                const pData = await permRes.json();
                setCapabilities(pData.capabilities || []);
            }
            if (usersRes.ok) {
                const uData = await usersRes.json();
                setUsers(uData);
            }
        } catch (error) {
            console.error("Error fetching permissions data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePermission = async (userId, capability, currentState) => {
        // Optimistic UI update
        const updatedUsers = users.map(user => {
            if (user.id === userId) {
                const currentCaps = user.permissions.capabilities || [];
                const newCaps = currentState
                    ? currentCaps.filter(c => c !== capability) // Remove
                    : [...currentCaps, capability]; // Add

                return {
                    ...user,
                    permissions: {
                        ...user.permissions,
                        capabilities: newCaps
                    }
                };
            }
            return user;
        });

        setUsers(updatedUsers);
        setSavingUserId(userId);

        // Find the newly updated user to send full capabilities payload
        const userToSave = updatedUsers.find(u => u.id === userId);

        try {
            const res = await authFetch(`/api/backoffice/users/${userId}/permissions`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ capabilities: userToSave.permissions.capabilities })
            });

            if (res.ok) {
                showSaveStatus(userId, true);
            } else {
                throw new Error("Failed to save");
            }
        } catch (err) {
            console.error("Error saving permissions:", err);
            // Revert optimistic update on failure by refetching or reversing manually
            fetchData();
            showSaveStatus(userId, false);
        } finally {
            setSavingUserId(null);
        }
    };

    const showSaveStatus = (userId, success) => {
        setSaveStatus({ userId, success });
        setTimeout(() => setSaveStatus({ userId: null, success: false }), 2000);
    };

    const filteredUsers = users.filter(u =>
        (u.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-slate-900/50 p-6 rounded-2xl border border-slate-800">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Shield className="text-primary" /> Permisos de Usuarios
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">Habilita o deshabilita accesos funcionales para cada agente del sistema.</p>
                </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl flex flex-col h-[calc(100vh-250px)]">
                {/* Fixed Top Section: Search */}
                <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex-shrink-0">
                    <div className="relative max-w-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-slate-500" />
                        </div>
                        <input
                            type="text"
                            placeholder="Buscar usuario por nombre o email..."
                            className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-200 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Scrollable Container for the Matrix */}
                <div className="flex-1 overflow-auto custom-scrollbar relative">
                    {loading ? (
                        <div className="p-8 text-center text-slate-500 animate-pulse">Cargando matriz de permisos...</div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">No se encontraron usuarios.</div>
                    ) : (
                        <table className="w-full text-left border-collapse min-w-max">
                            <thead className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur shadow-sm">
                                <tr>
                                    <th className="p-4 border-b border-slate-800 font-semibold text-slate-300 min-w-[250px] sticky left-0 z-20 bg-slate-900/95 backdrop-blur">
                                        Usuario
                                    </th>
                                    {capabilities.map(cap => (
                                        <th key={cap} className="p-4 border-b border-slate-800 font-mono text-[10px] uppercase tracking-wider text-slate-500 text-center w-32 whitespace-nowrap">
                                            <div className="transform -rotate-12 origin-bottom-left inline-block mb-2">
                                                {['gestion_facturas', 'admin_esave'].includes(cap) ? <span className="normal-case">{cap === 'admin_esave' ? 'Admin ESave' : 'ESave'}</span> : cap.replace(/_/g, ' ')}
                                            </div>
                                        </th>
                                    ))}
                                    <th className="p-4 border-b border-slate-800 w-10"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/50">
                                {filteredUsers.map(user => {
                                    const userCaps = user.permissions?.capabilities || [];
                                    const isSaving = savingUserId === user.id;
                                    const status = saveStatus.userId === user.id ? (saveStatus.success ? 'success' : 'error') : null;

                                    return (
                                        <tr key={user.id} className="hover:bg-slate-800/20 transition-colors group">
                                            {/* User Info Column (Sticky) */}
                                            <td className="p-4 bg-slate-900 group-hover:bg-slate-800/50 sticky left-0 z-10 transition-colors border-r border-transparent group-hover:border-slate-800/50">
                                                <div className="font-bold text-slate-200 text-sm">{user.full_name || 'Sin Nombre'}</div>
                                                <div className="text-xs text-slate-500 truncate">{user.email}</div>
                                                {userCaps.includes('backoffice') && (
                                                    <span className="mt-1 inline-block bg-primary/10 text-primary border border-primary/20 text-[9px] uppercase px-1.5 py-0.5 rounded font-bold">Admin</span>
                                                )}
                                            </td>

                                            {/* Capabilities Toggles */}
                                            {capabilities.map(cap => {
                                                const hasCap = userCaps.includes(cap);
                                                return (
                                                    <td key={cap} className="p-4 text-center align-middle">
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                className="sr-only peer"
                                                                checked={hasCap}
                                                                onChange={() => handleTogglePermission(user.id, cap, hasCap)}
                                                                disabled={isSaving}
                                                            />
                                                            <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary opacity-80 peer-hover:opacity-100 disabled:opacity-50"></div>
                                                        </label>
                                                    </td>
                                                );
                                            })}

                                            {/* Status Indicator */}
                                            <td className="p-4 text-center w-10">
                                                {isSaving ? (
                                                    <Save size={16} className="text-slate-500 animate-pulse mx-auto" />
                                                ) : status === 'success' ? (
                                                    <CheckCircle2 size={16} className="text-emerald-500 mx-auto" />
                                                ) : status === 'error' ? (
                                                    <AlertCircle size={16} className="text-red-500 mx-auto" />
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PermissionsGrid;
